import React, { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Divider } from '@chakra-ui/react';
import { isEmpty, isEqual } from 'lodash';
import { Button } from '@/components/common/Button';
import { Icon, IconSize } from '@/components/common/Icon';
import { SubHeaderPanel } from '@/components/common/SubHeaderPanel';
import type { FiltersState } from '@/core/modules/MapPanelSources';
import { useFiltersContext } from '../../context/FiltersContextProvider';
import { PanelType, useLeftSidePanelContext } from '../../context/LeftSidePanelContextProvider';
import { useDemoActionMenu } from '../../hooks/useDemoActionMenu';
import { useUserIncidentsColorSchemes } from '../../hooks/useUserIncidentsColorSchemes';
import styles from './SubHeaderControls.module.scss';
import { NewLinesDocsButtons } from './components/NewLinesDocsButtons';

export const SubHeaderControls = () => {
  const t = useTranslations('incidents_page');
  const { filters } = useFiltersContext();
  const { openPanel, panel, closePanel } = useLeftSidePanelContext();
  const { figmaLink, withDocsButtons, actionMenuLink } = useDemoActionMenu();
  const { availableSchemes } = useUserIncidentsColorSchemes();

  const handleButtonClick = useCallback(
    (type: PanelType) => () => (type === panel ? closePanel() : openPanel(type)),
    [panel],
  );
  const filtersCount = useMemo(
    () =>
      Object.entries(filters).filter(
        ([key, value]) => (key as keyof FiltersState) !== 'dateRange' && Array.isArray(value) && value.length > 0,
      ).length,
    [filters],
  );
  const filtersLabelWithCount = `${t('filter')}${filtersCount ? ` (${filtersCount})` : ''}`;
  const getPanelLabel = useCallback(
    (panelType: PanelType, label: string) => (isEqual(panel, panelType) ? `${t('hide')} ${label}` : label),
    [panel, t],
  );

  return (
    <SubHeaderPanel className={styles.root}>
      <div className={styles.leftControls}>
        <Button
          className={styles.button}
          size="md"
          icon={<Icon name="NewspaperIcon" size={IconSize.s16} />}
          onClick={handleButtonClick(PanelType.IncidentsFeed)}
        >
          {getPanelLabel(PanelType.IncidentsFeed, t('feed'))}
        </Button>
        <Button
          className={styles.button}
          size="md"
          icon={<Icon name="FilterIcon" size={IconSize.s16} />}
          onClick={handleButtonClick(PanelType.Filters)}
        >
          {isEqual(panel, PanelType.Filters) ? `${t('hide')} ${t('filter')}` : filtersLabelWithCount}
        </Button>
        {!isEmpty(availableSchemes) && (
          <Button
            className={styles.button}
            size="md"
            icon={<Icon name="MapIcon" size={IconSize.s16} />}
            onClick={handleButtonClick(PanelType.MapView)}
          >
            {getPanelLabel(PanelType.MapView, t('map_view'))}
          </Button>
        )}
        {withDocsButtons && (
          <>
            <Divider variant="vertical" className={styles.divider} />
            <NewLinesDocsButtons />
          </>
        )}
      </div>
      <div className={styles.rightControls}>
        {actionMenuLink && (
          <>
            <Divider variant="vertical" className={styles.divider} style={{ marginRight: 8 }} />
            <Button
              className={styles.button}
              size="md"
              icon={<Icon name="DocumentMapIcon" size={IconSize.s16} />}
              onClick={() => window.open(actionMenuLink, '_blank')}
            >
              {t('action_menu')}
            </Button>
          </>
        )}
        {figmaLink && (
          <Button
            className={styles.button}
            size="md"
            onClick={() => window.open(figmaLink, '_blank')}
            icon={<Icon name="TradeIcon" size={IconSize.s16} />}
          >
            {t('trade_flows')}
          </Button>
        )}
      </div>
    </SubHeaderPanel>
  );
};
